import React, { useState, useEffect } from 'react';
import {
  Button,
  Card,
  CardHeader,
  CardBody,
  NavItem,
  NavLink,
  Nav,
  Row,
  Col,
  Container
} from "reactstrap";
import { Line, Pie, Bar } from "react-chartjs-2";  // Make sure you're importing the correct chart components
import classnames from "classnames";
import {
  chartOptions,
  parseOptions,
  chartExample1,
  chartExample2,
  PieChartOption,
  furnitureBarOption,
  BarChart
} from "variables/charts.js";
import Header from "components/Headers/Header.js";
import { get } from "utils/api";
import { Chart } from "chart.js"

const Index = (props) => {
  const [activeNav, setActiveNav] = useState(1);
  const [chartData, setChartData] = useState({
    labels: [],
    datasets: [],
  });
  const [cleaningChartData, setCleaningChartData] = useState({});
  const [furnitureChartData, setFurnitureChartData] = useState({});
  const [chartResData, setChartResData] = useState({});
  const [isLoader, setIsLoader] = useState(false)

  // Function to generate the dynamic chart data for cleaning data
  const generateChartData = (cleaningData) => {
    const getColor = (frequency) => {
      const red = Math.min(255, Math.floor(frequency / 3));  // Dynamically adjust color
      const green = Math.min(255, Math.floor(255 - frequency / 3));
      const blue = 150;
      return `rgb(${red}, ${green}, ${blue})`;
    };

    return {
      labels: cleaningData.map(item => item.categoryId),
      datasets: [
        {
          data: cleaningData.map(item => item.totalFrequency),
          backgroundColor: cleaningData.map(item => getColor(item.totalFrequency)),
          hoverOffset: 4,
        },
      ],
    };
  };

  useEffect(() => {
    async function fetchData() {
      try {
        setIsLoader(true)
        const res = await get("/api/admin/get/dashData");
        const data = res?.data;
        if (data) {
          const yearlyData = {
            labels: data?.salesValueData?.yearly?.map(item => item.month),
            data: data?.salesValueData?.yearly?.map(item => item.totalAmount),
          };
          const dailyData = {
            labels: data?.salesValueData?.daily?.map(item => item.date),
            data: data?.salesValueData?.daily?.map(item => item.totalAmount),
          };
          const yearlyCount = {
            labels: data?.salesValueData?.yearlyCount?.map(item => item.month),
            data: data?.salesValueData?.yearlyCount?.map(item => item.totalCount),
          };
          const totalCleaningPackage = data?.salesValueData?.cleaningData?.reduce((acc, item) => acc + item.totalFrequency, 0);
          const totalFurniturePackage = data?.salesValueData?.furnitureData?.reduce((acc, item) => acc + item.totalQuantity, 0);

          setCleaningChartData(generateChartData(data?.salesValueData?.cleaningData));
          setFurnitureChartData(BarChart(data?.salesValueData?.furnitureData));  // Assuming BarChart is a function that generates bar chart data

          setChartResData({
            yearlyData,
            dailyData,
            yearlyCount,
            totalCleaningPackage,
            totalFurniturePackage,
          });

          setChartData({
            labels: [...yearlyData.labels],
            datasets: [
              {
                label: "Yearly Sales Data",
                data: [...yearlyData.data],
              },
            ],
          });
        } else {
          console.error("No data found.");
        }
        setIsLoader(false)
      } catch (error) {
        console.error("Error fetching data:", error);
        setIsLoader(false)
      }
    }
    fetchData();
  }, []);

  if (window.Chart) {
    parseOptions(Chart, chartOptions());
  }

  const toggleNavs = (index, label) => {
    // e.preventDefault();
    setActiveNav(index);
    setChartData({
      labels: [...chartResData[label]?.labels] || [],
      datasets: [
        {
          label: "Sales Data",
          data: [...chartResData[label]?.data] || [],
        },
      ],
    });
  };

  return (
    <>
      <Header />
      {isLoader ? (
        <div
          className="d-flex justify-content-center align-items-center"
          style={{ height: "40%" }}
        >
          <div className="spinner-border text-light" role="status">
            <span className="sr-only">Loading...</span>
          </div>
        </div>

      ) : (<Container className="mt--7" fluid>
        <Row>
          <Col className="mb-5 mb-xl-0" xl="8">
            <Card className="bg-gradient-default shadow">
              <CardHeader className="bg-transparent">
                <Row className="align-items-center">
                  <div className="col">
                    <h6 className="text-uppercase text-light ls-1 mb-1">Overview</h6>
                    <h2 className="text-white mb-0">Sales Value</h2>
                  </div>
                  <div className="col">
                    <Nav className="justify-content-end" pills>
                      <NavItem>
                        <NavLink
                          className={classnames("py-2 px-3", { active: activeNav === 1 })}
                          onClick={(e) => toggleNavs(1, "yearlyData")}
                        >
                          <span className="d-none d-md-block">Year</span>
                          <span className="d-md-none">M</span>
                        </NavLink>
                      </NavItem>
                      <NavItem>
                        <NavLink
                          className={classnames("py-2 px-3", { active: activeNav === 2 })}
                          data-toggle="tab"
                          onClick={(e) => toggleNavs(2, "dailyData")}
                        >
                          <span className="d-none d-md-block">Month</span>
                          <span className="d-md-none">W</span>
                        </NavLink>
                      </NavItem>
                      <NavItem>
                        <NavLink
                          className={classnames("py-2 px-3", { active: activeNav === 3 })}
                          data-toggle="tab"
                          onClick={(e) => toggleNavs(3, "yearlyCount")}
                        >
                          <span className="d-none d-md-block">Total Order</span>
                          <span className="d-md-none">T</span>
                        </NavLink>
                      </NavItem>
                    </Nav>
                  </div>
                </Row>
              </CardHeader>
              <CardBody>
                <div className="chart">
                  <Line
                    data={chartData}
                    options={activeNav === 3 ? chartExample2.options : chartExample1.options}
                    getDatasetAtEvent={(e) => console.log(e)}
                  />
                </div>
              </CardBody>
            </Card>
          </Col>
          <Col xl="4">
            <Card className="shadow">
              <CardHeader className="bg-transparent">
                <Row className="align-items-center">
                  <div className="col">
                    <h6 className="text-uppercase text-muted ls-1 mb-1">Performance</h6>
                    <h2 className="mb-0">Total Cleaning Package: {chartResData.totalCleaningPackage}</h2>
                  </div>
                </Row>
              </CardHeader>
              <CardBody>
                <div className="chart">
                  <Pie data={cleaningChartData} options={PieChartOption} />
                </div>
              </CardBody>
            </Card>
          </Col>
        </Row>
        {/* <Row className="mt-5 mb-4">
          <Col xl="12">
            <Card className="shadow">
              <CardHeader className="bg-transparent">
                <Row className="align-items-center">
                  <div className="col">
                    <h6 className="text-uppercase text-muted ls-1 mb-1">Monthly Furniture Data (Last 12 Months)</h6>
                    <h2 className="mb-0">Total Quantity: {chartResData.totalFurniturePackage}</h2>
                  </div>
                </Row>
              </CardHeader>
              <CardBody>
                <div className="chart">
                  <Bar data={furnitureChartData} options={furnitureBarOption} />
                </div>
              </CardBody>
            </Card>
          </Col>
        </Row> */}
      </Container>)}

    </>
  );
};

export default Index;
